import type { SceneGroup } from '@lumen5/luminary';

import { getVoiceDrivenAssetDurationAfterCutsApplied } from 'js/src/luminary/luminary-selectors';
import voicedrivenSceneGroupDurationFixerSchedule from 'js/src/luminary/voicedriven-scenegroup-duration-fixer-schedule';

/**
 * The applied source data duration is usually a rounded off value
 * and we only get the 'real' duration of the asset after it has loaded.
 *
 * When this happens, we want to make sure that the scene duration matches
 * the loaded asset duration, so we add the diff to the last scene.
 *
 * If we don't do this, we end up with a scene group longer than the media
 * which results in the base scene design showing from the end time
 * of the last transcript word till the end of the scene group.
 */
export const updateSceneGroupDurationToEqualVoiceDrivenAssetDuration = async ({
    sceneGroup,
    waitForLuminary = true,
}: {
    sceneGroup: SceneGroup;
    waitForLuminary?: boolean;
}) => {
    voicedrivenSceneGroupDurationFixerSchedule.increment(sceneGroup.id);

    if (waitForLuminary) {
        const luminary = sceneGroup.root;
        await luminary.onCanPlay();
    }
    if (sceneGroup.destroyed) {
        return; // fine, the scene was destroyed before the asset loaded
    }

    const sceneGroupDuration = sceneGroup.duration;
    const voiceDrivenAssetDuration = getVoiceDrivenAssetDurationAfterCutsApplied(sceneGroup);
    if (voiceDrivenAssetDuration === 0) {
        // things are obviously not set up yet.
        voicedrivenSceneGroupDurationFixerSchedule.decrement(sceneGroup.id);
        return;
    }

    const durationDiff = voiceDrivenAssetDuration - sceneGroupDuration;

    const lastScene = sceneGroup.scenes.at(-1);
    if (!lastScene) {
        throw new Error('No last scene found');
    }
    lastScene.duration += durationDiff;

    voicedrivenSceneGroupDurationFixerSchedule.decrement(sceneGroup.id);
};
